import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogContentText,
  List,
  ListItem,
  IconButton,
  ListItemText,
  Stack,
  Fab,
  CircularProgress
} from '@mui/material';
import { Icon } from '@iconify/react';
import trashFill from '@iconify/icons-eva/trash-2-fill';
import editFill from '@iconify/icons-eva/edit-2-fill';
import plusFill from '@iconify/icons-eva/plus-fill';
import closeFill from '@iconify/icons-eva/close-fill';
import { grey } from '@mui/material/colors';
// context
import { TicketsContext } from '../../../contexts/Tickets';
import { useAuth } from '../../../contexts/Auth';
import { SettingsContext } from '../../../contexts/Settings';
// components
import TicketVoucherFormDialog from './form/dialog';
// service
import { deleteVoucher } from '../../../services/Ticket';
import { updateInventoryMany } from '../../../services/Inventory';

TicketVouchersDialog.propTypes = {
  openTicketVouchersDialog: PropTypes.bool.isRequired,
  setOpenTicketVouchersDialog: PropTypes.func.isRequired,
  ticket: PropTypes.object
};

export default function TicketVouchersDialog({
  openTicketVouchersDialog,
  setOpenTicketVouchersDialog,
  ticket
}) {
  const { authUser } = useAuth();
  const { authSettings } = useContext(SettingsContext);
  const { setTickets } = useContext(TicketsContext);

  const [isLoading, setIsLoading] = useState(false);
  const [focusVoucher, setFocusVoucher] = useState();
  const [openTicketVoucherFormDialog, setOpenTicketVoucherFormDialog] = useState(false);

  const handleClose = () => {
    setOpenTicketVouchersDialog(false);
  };

  const handleOpenTicketVouchersFormDialog = (voucher) => {
    setFocusVoucher(voucher);
    setOpenTicketVoucherFormDialog(true);
  };

  const handleTicketVoucherDelete = async (voucher) => {
    if (!window.confirm(`Do you really want to delete this voucher: "${voucher.code}"?`)) {
      return;
    }

    setIsLoading(true);

    try {
      const idToken = await authUser.getIdToken();
      const headers = {
        Authorization: `Bearer ${idToken}`,
        'Account-Key': authSettings.server.path
      };

      await deleteVoucher({
        headers,
        eventId: ticket.event._id,
        sectionId: ticket.section._id,
        ticketId: ticket._id,
        voucherId: voucher._id
      });

      setTickets((tickets) => {
        tickets[ticket.section._id] = tickets[ticket.section._id].map((currentTicket) => {
          if (currentTicket._id === ticket._id) {
            currentTicket.vouchers = currentTicket.vouchers.filter(
              (currentVoucher) => currentVoucher._id !== voucher._id
            );
          }
          return currentTicket;
        });

        return tickets;
      });

      // TODO: Atualizar usando kafka (provisório)
      const payloadServices = {
        ticket: {
          _id: ticket._id,
          name: ticket.name,
          reference: ticket.reference,
          limit: ticket.limit,
          price: ticket.price,
          voucher: {
            _id: voucher._id,
            action: 'delete'
          }
        }
      };

      await updateInventoryMany({ headers, payload: payloadServices });
    } catch (err) {
      console.error(err);
      alert('An error occurred! Please, try again.');
    }

    setIsLoading(false);
  };

  return (
    <>
      <Dialog open={openTicketVouchersDialog} fullWidth scroll="body" maxWidth="sm" sx={{ m: -2 }}>
        <DialogTitle sx={{ px: { xs: 2, sm: 3 } }}>
          <Stack spacing={2} direction="row" alignItems="center" justifyContent="space-between">
            Vouchers - {ticket?.name}
            <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
              <Icon icon={closeFill} width="24" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={3}>
              <CircularProgress color="inherit" />
            </Box>
          ) : (
            <>
              <DialogContentText>Set vouchers to be used on this ticket only</DialogContentText>
              {ticket?.vouchers?.length ? (
                <List sx={{ mt: 2 }}>
                  {ticket.vouchers.map((voucher) => (
                    <Box
                      key={voucher._id}
                      sx={{
                        background: grey[100],
                        borderRadius: '20px',
                        mb: 1,
                        p: 1,
                        px: { xs: 0, sm: 1 }
                      }}
                    >
                      <ListItem
                        secondaryAction={
                          <>
                            <IconButton
                              aria-label="edit"
                              onClick={() => handleOpenTicketVouchersFormDialog(voucher)}
                            >
                              <Icon icon={editFill} />
                            </IconButton>

                            <IconButton
                              aria-label="delete"
                              onClick={() => handleTicketVoucherDelete(voucher)}
                            >
                              <Icon icon={trashFill} />
                            </IconButton>
                          </>
                        }
                      >
                        <ListItemText
                          sx={{ width: '55%' }}
                          primary={voucher.code}
                          secondary={`Discount ${voucher.discount}% | Limit ${voucher.limit}`}
                        />
                      </ListItem>
                    </Box>
                  ))}
                  <Box sx={{ textAlign: 'center' }}>
                    <Fab
                      size="medium"
                      color="primary"
                      aria-label="add"
                      sx={{ flexShrink: 0 }}
                      onClick={handleOpenTicketVouchersFormDialog}
                    >
                      <Icon icon={plusFill} width="24" />
                    </Fab>
                  </Box>
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', p: 5 }}>
                  <Typography variant="h5" sx={{ mb: 2 }}>
                    No voucher yet!
                    <Typography
                      component="span"
                      variant="body2"
                      color="grey.500"
                      sx={{ display: 'block' }}
                    >
                      Create your first voucher
                    </Typography>
                  </Typography>
                  <Fab
                    size="medium"
                    color="primary"
                    aria-label="add"
                    sx={{ flexShrink: 0 }}
                    onClick={handleOpenTicketVouchersFormDialog}
                  >
                    <Icon icon={plusFill} width="24" />
                  </Fab>
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <TicketVoucherFormDialog
        openTicketVoucherFormDialog={openTicketVoucherFormDialog}
        setOpenTicketVoucherFormDialog={setOpenTicketVoucherFormDialog}
        ticket={ticket}
        voucher={focusVoucher}
      />
    </>
  );
}
